import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { applyForCareer } from '../../../api/action/career.jsx';
import { Login, Signup } from '../../../domains/common/views';
import {
  careerApplyHref,
  formatCareerDate,
  getCareerDescription,
  getEmploymentLabel,
  getWorkModeLabel,
  useCareerOpenings,
} from '../../../hooks/useCareerOpenings.jsx';
import { sanitizeStyleValue } from '../../Sanitizer.jsx';
import { useTemplateContactInfo, useTemplateInquiryForm } from '../../TemplateContactUtils.jsx';
import {
  applyFutureSectionStyle,
  getActions,
  getBlocks,
  getImageUrl,
} from '../shared/templateSectionUtils.jsx';
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  Facebook,
  Instagram,
  Linkedin,
  Link,
  MapPin,
  Twitter,
  Youtube,
} from 'lucide-react';
const waasTheme = (theme = {}) => ({
  primary: '#07111f',
  secondary: '#d8c2b6',
  accent: '#0b0f16',
  background: '#e6e8eb',
  surface: '#ffffff',
  color: '#0b0f16',
  mutedColor: '#5f6670',
  borderColor: '#d5d8dd',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  baseFontSize: '14px',
  pageWidth: '1120px',
  sectionSpacing: '72px 24px',
  buttonRadius: '999px',
  buttonHeight: '42px',
  cardRadius: '24px',
  cardShadow: '0 18px 42px rgba(24, 34, 46, 0.10)',
  inputRadius: '14px',
  imageRadius: '24px',
  ...theme
});

const waasSocialIconMap = {
  Facebook,
  Instagram,
  Linkedin,
  Link,
  Twitter,
  Youtube
};

const waasSocialPlatformOptions = {
  facebook: { label: 'Facebook', icon: 'Facebook', baseUrl: 'https://www.facebook.com/' },
  instagram: { label: 'Instagram', icon: 'Instagram', baseUrl: 'https://www.instagram.com/' },
  linkedin: { label: 'LinkedIn', icon: 'Linkedin', baseUrl: 'https://www.linkedin.com/company/' },
  twitter: { label: 'X', icon: 'Twitter', baseUrl: 'https://x.com/' },
  youtube: { label: 'YouTube', icon: 'Youtube', baseUrl: 'https://www.youtube.com/@' }
};

const normalizeWaasSocialPlatform = (value = '') => {
  const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (['facebook', 'fb'].includes(normalized)) return 'facebook';
  if (['instagram', 'insta', 'ig'].includes(normalized)) return 'instagram';
  if (['linkedin', 'linkedln', 'iinkedin'].includes(normalized)) return 'linkedin';
  if (['twitter', 'x', 'xcom'].includes(normalized)) return 'twitter';
  if (['youtube', 'yt'].includes(normalized)) return 'youtube';
  return '';
};

const formatWaasSocialRoute = (value = '', platformKey = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed === '#') return '';
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed) || /\.[a-z]{2,}(\/|$)/i.test(trimmed)) return `https://${trimmed}`;
  const baseUrl = waasSocialPlatformOptions[platformKey]?.baseUrl;
  if (!baseUrl) return trimmed;
  return `${baseUrl}${trimmed.replace(/^@/, '').replace(/^\/+/, '')}`;
};

const waasSocialLinksFromCompany = (company = {}) => {
  const accounts = Array.isArray(company?.socialMediaAccounts)
    ? company.socialMediaAccounts
    : Array.isArray(company?.companyDetails?.socialMediaAccounts)
      ? company.companyDetails.socialMediaAccounts
      : [];
  return accounts.map((account) => {
    const platformKey = normalizeWaasSocialPlatform(account?.name || account?.label || account?.platform || account?.type || account?.key);
    const route = formatWaasSocialRoute(account?.value || account?.route || account?.url || account?.href || account?.link, platformKey);
    if (!platformKey || !route) return null;
    const config = waasSocialPlatformOptions[platformKey];
    return {
      label: account?.label || config.label,
      route,
      icon: config.icon,
      platformKey,
      visible: account?.visible !== false
    };
  }).filter(Boolean);
};

const normalizeWaasSocialLinks = (items, company = {}) => {
  const byPlatform = new Map();
  const customLinks = [];
  const hasConfiguredList = Array.isArray(items);
  const configuredItems = (hasConfiguredList ? items : []).filter((item) => item?.visible !== false);
  configuredItems.forEach((item) => {
    const platformKey = normalizeWaasSocialPlatform(item?.platformKey || item?.icon || item?.label || item?.name);
    const route = formatWaasSocialRoute(item?.route || item?.url || item?.href || item?.value, platformKey);
    if (!route) return;
    const config = waasSocialPlatformOptions[platformKey];
    const link = {
      ...item,
      label: item?.label || item?.name || config?.label || 'Social link',
      route,
      icon: config?.icon || item?.icon || 'Link',
      platformKey,
      visible: item?.visible !== false
    };
    if (platformKey) {
      byPlatform.set(platformKey, link);
    } else {
      customLinks.push(link);
    }
  });
  const configuredPlatformKeys = new Set(configuredItems.map((item) => normalizeWaasSocialPlatform(item?.platformKey || item?.icon || item?.label || item?.name)).filter(Boolean));
  waasSocialLinksFromCompany(company).forEach((item) => {
    if (hasConfiguredList && !configuredPlatformKeys.has(item.platformKey)) return;
    byPlatform.set(item.platformKey, { ...(byPlatform.get(item.platformKey) || {}), ...item });
  });
  return [...byPlatform.values(), ...customLinks];
};

const WaasSocialIcon = ({ item, size = 14 }) => {
  const Icon = waasSocialIconMap[item?.icon] || Link;
  return <Icon size={size} strokeWidth={2.3} aria-hidden="true" />;
};

const normalizeWaasLinkConfig = (source = {}, fallbackUrl = '') => ({
  type: source?.link?.type || (fallbackUrl && /^(https?:|mailto:|tel:|www\.)/i.test(String(fallbackUrl)) ? 'external' : 'internal'),
  url: source?.link?.url ?? fallbackUrl ?? source?.url ?? source?.route ?? '',
  target: source?.link?.target || (source?.newTab ? '_blank' : '_self'),
  documentAction: source?.link?.documentAction || 'open'
});

const resolveWaasLinkHref = (source = {}, fallbackUrl = '') => {
  const link = normalizeWaasLinkConfig(source, fallbackUrl);
  const url = String(link.url || '').trim();
  if (!url) return '#';
  if (link.type === 'external' && !/^(https?:|mailto:|tel:)/i.test(url) && !url.startsWith('//')) {
    return `https://${url.replace(/^\/+/, '')}`;
  }
  if (link.type === 'internal' && url !== '#' && !url.startsWith('/') && !url.startsWith('#')) {
    return `/${url.replace(/^\/+/, '')}`;
  }
  return url;
};

const waasLinkProps = (source = {}, fallbackUrl = '') => {
  const link = normalizeWaasLinkConfig(source, fallbackUrl);
  const target = link.target === '_blank' ? '_blank' : undefined;
  return {
    href: resolveWaasLinkHref(source, fallbackUrl),
    target,
    rel: target ? 'noopener noreferrer' : undefined,
    download: link.type === 'document' && link.documentAction === 'download' ? '' : undefined
  };
};

const WaasLinkedElement = ({ item = {}, fallbackUrl = '', children, style }) => {
  const link = normalizeWaasLinkConfig(item, fallbackUrl);
  if (!link.url) return children;
  return (
    <a {...waasLinkProps(item, fallbackUrl)} style={{ display: 'block', color: 'inherit', textDecoration: 'none', ...style }}>
      {children}
    </a>
  );
};

const templateOverlayColorWithOpacity = (color = '#000000', opacity = 0.45) => (
  String(color).startsWith('#')
    ? `${sanitizeStyleValue(color)}${Math.round(Number(opacity || 0) * 255).toString(16).padStart(2, '0')}`
    : sanitizeStyleValue(color)
);

const waasSectionStyle = (section, theme = {}) => {
  const t = waasTheme(theme);
  const style = section?.style || {};
  const next = {
    backgroundColor: section?.style?.backgroundColor || t.background,
    color: section?.style?.color || t.color,
    padding: section?.style?.padding || t.sectionSpacing || '76px 20px',
    minHeight: section?.style?.minHeight,
    margin: section?.style?.margin,
    borderRadius: section?.style?.borderRadius,
    border: section?.style?.border,
    boxShadow: section?.style?.boxShadow,
    textAlign: style.textAlign || section?.layout?.align,
    overflow: 'hidden'
  };
  if (style.backgroundType === 'gradient' && style.backgroundGradient) {
    next.background = sanitizeStyleValue(style.backgroundGradient);
  }
  if (style.backgroundImage && style.backgroundType !== 'none') {
    const overlay = templateOverlayColorWithOpacity(style.overlayColor || '#000000', style.overlayOpacity || 0.45);
    next.backgroundImage = `${style.backgroundType === 'image' && style.overlayOpacity !== '0' ? `linear-gradient(${overlay}, ${overlay}), ` : ''}url(${sanitizeStyleValue(style.backgroundImage)})`;
    next.backgroundSize = sanitizeStyleValue(style.backgroundSize || 'cover');
    next.backgroundPosition = sanitizeStyleValue(style.backgroundPosition || 'center');
    next.backgroundRepeat = sanitizeStyleValue(style.backgroundRepeat || 'no-repeat');
  }
  if (style.backgroundType === 'none') {
    delete next.background;
    delete next.backgroundColor;
    delete next.backgroundImage;
  }
  return applyFutureSectionStyle(next, section, t);
};

const waasContainerStyle = (section, theme = {}) => {
  const t = waasTheme(theme);
  return {
    maxWidth: section?.layout?.width === 'full'
      ? 'none'
      : section?.layout?.width === 'narrow'
        ? section?.style?.maxWidth || '920px'
        : section?.style?.maxWidth || t.pageWidth,
    margin: '0 auto',
    width: '100%',
    // Establish a query container so fluid heading sizes (cqw) scale with this
    // capped design-width container instead of the raw viewport (vw). This makes the
    // published page render at the same calm size as the builder preview and stops the
    // hero/headings ballooning + overflowing on wide monitors. (Parity fix.)
    containerType: 'inline-size',
  };
};

const waasFlowImageUrl = (item = {}) => getImageUrl(item);

const waasIsLightColor = (color) => {
  if (typeof color !== 'string') return false;
  const s = color.trim();
  const hex = s.replace('#', '');
  let r;
  let g;
  let b;
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    r = parseInt(hex.slice(0, 2), 16); g = parseInt(hex.slice(2, 4), 16); b = parseInt(hex.slice(4, 6), 16);
  } else if (/^[0-9a-f]{3}$/i.test(hex)) {
    r = parseInt(hex[0] + hex[0], 16); g = parseInt(hex[1] + hex[1], 16); b = parseInt(hex[2] + hex[2], 16);
  } else {
    const match = s.match(/rgba?\(([^)]+)\)/i);
    if (!match) return false;
    const parts = match[1].split(',').map((n) => parseInt(n, 10));
    [r, g, b] = parts;
  }
  if (![r, g, b].every((v) => Number.isFinite(v))) return false;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62;
};

const waasFlowEyebrowStyle = (section, theme) => ({
  margin: '0 0 18px',
  color: section?.style?.accentColor || theme.primary || '#07111f',
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '.14em',
  textTransform: 'uppercase'
});

const waasFlowHeadingStyle = (theme, size = 'clamp(44px, 5cqw, 72px)') => ({
  margin: 0,
  fontFamily: theme.headingFontFamily || 'Inter, Arial, sans-serif',
  fontWeight: theme.headingWeight || 300,
  color: theme.accent || '#0b0f16',
  fontSize: size,
  lineHeight: .96,
  letterSpacing: 0
});

const WaasButton = ({ action, section, theme }) => {
  if (!action?.label) return null;
  const t = waasTheme(theme);
  const variant = action.variant || section?.content?.buttonVariant || 'primary';
  const color = action.color || section?.content?.buttonColor || section?.style?.accentColor || t.accent;
  const isOutline = variant === 'outline' || variant === 'ghost';
  return (
    <a
      {...waasLinkProps(action, action.url)}
      style={{
        display: 'inline-flex',
        minHeight: action.size === 'small' ? 34 : t.buttonHeight,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: action.radius || section?.content?.buttonRadius || t.buttonRadius,
        padding: action.size === 'small' ? '0 16px' : '0 24px',
        border: `1px solid ${isOutline ? 'rgba(11, 15, 22, .28)' : color}`,
        background: isOutline ? 'rgba(255, 255, 255, .36)' : color,
        color: action.textColor || (isOutline ? color : '#ffffff'),
        textDecoration: 'none',
        fontSize: action.size === 'small' ? 10 : 11,
        fontWeight: 800,
        letterSpacing: '.04em',
        textTransform: 'uppercase'
      }}
    >
      {action.label}
    </a>
  );
};

const waasVisibleButtons = (buttons) => (Array.isArray(buttons) ? buttons : [])
  .map((item, index) => ({ item, index }))
  .filter(({ item }) => item && item.visible !== false && (item.label || item.text || item.title));

export const renderWaasSectionExtras = (section = {}, theme = {}) => {
  const t = waasTheme(theme);
  const content = section.content || {};
  const buttons = waasVisibleButtons(content.buttons);
  const hasBadge = Boolean(content.trustBadgeLabel || content.actionHint);
  if (!buttons.length && !hasBadge) return null;
  return (
    <>
      {buttons.length > 0 && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 8 }}>
          {buttons.map(({ item, index }) => (
            <a
              key={`${item.label || 'button'}-${index}`}
              {...waasLinkProps(item, item.url)}
              style={{
                position: 'absolute',
                left: `${Number.isFinite(Number(item.x)) ? item.x : 50}%`,
                top: `${Number.isFinite(Number(item.y)) ? item.y : 50}%`,
                transform: 'translate(-50%, -50%)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: item.align || 'center',
                width: item.width || '180px',
                minHeight: item.height || t.buttonHeight || 42,
                padding: item.padding || '0 18px',
                borderRadius: item.radius || t.buttonRadius || '999px',
                border: item.border || '1px solid transparent',
                background: item.color || item.backgroundColor || t.accent || '#0b0f16',
                color: item.textColor || '#ffffff',
                textDecoration: 'none',
                textAlign: item.align || 'center',
                fontWeight: item.fontWeight || 800,
                fontSize: item.fontSize || 13,
                pointerEvents: 'auto',
                boxShadow: item.shadow || '0 10px 24px rgba(11,15,22,.18)',
                whiteSpace: 'normal'
              }}
            >
              {item.label || item.text || item.title || 'Button'}
            </a>
          ))}
        </div>
      )}
      {hasBadge && (
        <div style={{ maxWidth: section.style?.maxWidth || '1180px', margin: '0 auto', padding: '0 24px 20px', display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: section.layout?.align === 'center' ? 'center' : 'flex-start' }}>
          {content.trustBadgeLabel && <span style={{ border: '1px solid currentColor', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 800, opacity: .78 }}>{content.trustBadgeLabel}</span>}
          {content.actionHint && <span style={{ borderRadius: 999, padding: '6px 12px', fontSize: 12, background: 'rgba(3,3,3,.05)', opacity: .78 }}>{content.actionHint}</span>}
        </div>
      )}
    </>
  );
};

const WaasHeroSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const c = section.content || {};
  const imageUrl = getImageUrl(c);
  const heading = c.title || 'Designing digital systems that shape operations';
  return (
    <section
      style={{
        ...waasSectionStyle(section, t),
        padding: 0,
        minHeight: section.style?.minHeight || '640px',
        backgroundColor: section.style?.backgroundColor || '#2f6f8f',
        backgroundImage: imageUrl ? `linear-gradient(180deg, rgba(36, 100, 118, .18), rgba(36, 100, 118, .12) 44%, rgba(11,15,22,.18)), url(${imageUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: section.style?.backgroundPosition || 'center bottom',
        position: 'relative'
      }}
    >
      <div style={{ ...waasContainerStyle(section, t), minHeight: 'inherit', display: 'grid', alignItems: 'start', padding: '66px 24px 160px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto', textAlign: 'center', color: '#ffffff' }}>
          <h1 style={{ margin: 0, fontFamily: t.headingFontFamily, fontWeight: t.headingWeight || 300, fontSize: 'clamp(50px, 7.8cqw, 112px)', lineHeight: .96, letterSpacing: 0, color: '#ffffff', textShadow: '0 10px 34px rgba(20, 45, 58, .16)' }}>{heading}</h1>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28, margin: '22px auto 0', opacity: .72 }}>
            <span style={{ width: 214, maxWidth: '42%', height: 26, border: '1px solid rgba(255,255,255,.62)', borderRadius: 999, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, right: 12, top: 6, height: 1, background: 'rgba(255,255,255,.42)' }} />
              <span style={{ position: 'absolute', left: 18, right: 18, bottom: 6, height: 1, background: 'rgba(255,255,255,.42)' }} />
            </span>
            <span style={{ width: 170, maxWidth: '32%', height: 36, background: 'repeating-linear-gradient(120deg, rgba(255,255,255,.52) 0 2px, transparent 2px 12px)' }} />
          </div>
          {c.subtitle && <p style={{ maxWidth: 620, margin: '28px auto 0', color: 'rgba(255,255,255,.9)', fontSize: 17, lineHeight: 1.55 }}>{c.subtitle}</p>}
        </div>
      </div>
    </section>
  );
};

const WaasTrustedLogosSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        {section.content?.eyebrow && <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 42, color: t.accent, fontSize: 13, fontWeight: 800, letterSpacing: '.2em' }}><span style={{ width: 42, height: 1, background: t.accent }} />{section.content.eyebrow}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 54, overflow: 'hidden', whiteSpace: 'nowrap' }}>
          {getBlocks(section).map((item, index) => (
            <React.Fragment key={`${item.title}-${index}`}>
              {index > 0 && <span style={{ width: 7, height: 7, borderRadius: 999, background: '#d6d0c6' }} />}
              <span style={{ fontFamily: t.headingFontFamily, color: '#6f7378', fontSize: 38, opacity: index > 2 ? .35 : 1 }}>{item.title}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

const WaasAboutStorySection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const c = section.content || {};
  const blocks = getBlocks(section);
  const textBlocks = blocks.filter((item) => !waasFlowImageUrl(item)).slice(0, 2);
  const imageBlocks = blocks.filter((item) => waasFlowImageUrl(item));
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={{ ...waasContainerStyle(section, t), marginTop: section.style?.marginTop || 0, background: '#ffffff', borderRadius: 24, padding: 14, boxShadow: t.cardShadow, position: 'relative', zIndex: 3 }}>
        <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '1.02fr 1.48fr', gap: 14, alignItems: 'stretch' }}>
          <div style={{ padding: '48px 36px 40px', minHeight: 370, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              {c.eyebrow && <p style={waasFlowEyebrowStyle(section, t)}>{c.eyebrow}</p>}
              <h2 style={waasFlowHeadingStyle(t, 'clamp(44px, 5.2cqw, 68px)')}>{c.title}</h2>
            </div>
            <div style={{ maxWidth: 440 }}>
              {c.subtitle && <p style={{ margin: '0 0 14px', color: t.color, fontSize: 15, lineHeight: 1.45 }}>{c.subtitle}</p>}
              {c.body && <p style={{ margin: '0 0 24px', color: t.mutedColor, fontSize: 13, lineHeight: 1.58 }}>{c.body}</p>}
              <WaasButton action={{ label: c.primaryActionLabel, url: c.primaryActionUrl, variant: 'outline' }} section={section} theme={t} />
            </div>
          </div>
          <div className="waas-template-about-tile-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1.35fr', gap: 12 }}>
            {[...(textBlocks.length ? textBlocks : blocks.slice(0, 2)), ...imageBlocks].slice(0, 4).map((item, index) => {
              const imageUrl = waasFlowImageUrl(item);
              const imageCard = Boolean(imageUrl);
              return (
                <article key={`${item.title}-${index}`} style={{ minHeight: index > 1 ? 210 : 160, borderRadius: 16, overflow: 'hidden', background: imageCard ? '#d6dce0' : '#f9f9f8', padding: imageCard ? 0 : 22, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              {imageCard ? (
                <WaasLinkedElement item={item} fallbackUrl={item.url} style={{ height: '100%' }}>
                  <img src={imageUrl} alt={item.title || c.imageAlt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </WaasLinkedElement>
                  ) : (
                    <>
                      <h3 style={{ margin: 0, color: t.accent, fontSize: 30, lineHeight: 1, fontWeight: 400 }}>{item.title}</h3>
                      <p style={{ margin: 'auto 0 0', color: t.mutedColor, fontSize: 13, lineHeight: 1.5 }}>{item.description}</p>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const WaasPhilosophySection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={{ ...waasContainerStyle(section, t), textAlign: 'center', maxWidth: section.style?.maxWidth || 980 }}>
        <h2 style={{ margin: '0 0 36px', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(42px, 4.5cqw, 60px)' }}>{section.content?.title}</h2>
        <p style={{ margin: 0, color: t.color, fontSize: 'clamp(24px, 2.6cqw, 34px)', lineHeight: 1.45, fontStyle: 'italic' }}>{section.content?.quote}</p>
        {section.content?.author && <p style={{ margin: '26px 0 0', fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: section.style?.accentColor || t.primary }}>{section.content.author}</p>}
      </div>
    </section>
  );
};

const WaasWhyAvpsSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        <h2 style={{ margin: '0 0 78px', textAlign: 'center', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(40px, 4cqw, 58px)' }}>{section.content?.title}</h2>
        <div className="waas-template-benefit-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 54, textAlign: 'center' }}>
          {getBlocks(section).map((item, index) => (
            <article key={`${item.title}-${index}`}>
              <WaasLinkedElement item={item} fallbackUrl={item.url}>
                <div style={{ width: 78, height: 78, borderRadius: 999, background: '#fffdf8', margin: '0 auto 28px', display: 'grid', placeItems: 'center', color: section.style?.accentColor || t.primary, fontWeight: 800, fontSize: 18 }}>{item.icon || index + 1}</div>
                <h3 style={{ margin: 0, color: t.accent, fontSize: 18, fontWeight: 800 }}>{item.title}</h3>
              </WaasLinkedElement>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const WaasServicesGridSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const blocks = getBlocks(section);
  const featured = blocks.find((item) => waasFlowImageUrl(item)) || blocks[0] || {};
  const featuredImage = waasFlowImageUrl(featured);
  const metrics = Array.isArray(section.content?.metrics) && section.content.metrics.length
    ? section.content.metrics
    : [
      { label: 'Stack', value: featured.badge || 'Cloud' },
      { label: 'Status', value: 'Live' },
      { label: 'Support', value: '24/7' }
    ];
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          {section.content?.eyebrow && <p style={waasFlowEyebrowStyle(section, t)}>{section.content.eyebrow}</p>}
          <h2 style={waasFlowHeadingStyle(t, 'clamp(46px, 5.8cqw, 74px)')}>{section.content?.title}</h2>
        </div>
        <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '250px minmax(0, 1fr)', gap: 36, alignItems: 'stretch' }}>
          <div style={{ borderLeft: `1px solid ${t.accent}`, paddingLeft: 24, display: 'grid', alignContent: 'center', gap: 18, minHeight: 440 }}>
            {blocks.map((item, index) => (
              <a key={`${item.title}-${index}`} {...waasLinkProps(item, item.url)} style={{ textDecoration: 'none', color: index === 0 ? t.accent : t.mutedColor }}>
                <strong style={{ display: 'block', fontSize: 16, lineHeight: 1.2 }}>{item.title}</strong>
                {item.badge && <span style={{ display: 'block', marginTop: 5, fontSize: 11 }}>{item.badge}</span>}
              </a>
            ))}
          </div>
          <article style={{ minHeight: 460, borderRadius: 26, overflow: 'hidden', position: 'relative', background: '#ffffff', boxShadow: '0 18px 44px rgba(24,34,46,.12)' }}>
          {featuredImage && (
            <WaasLinkedElement item={featured} fallbackUrl={featured.url} style={{ position: 'absolute', inset: 0 }}>
              <img src={featuredImage} alt={featured.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </WaasLinkedElement>
          )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(11,15,22,.03), rgba(11,15,22,.56))' }} />
            <div style={{ position: 'relative', minHeight: 'inherit', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 26 }}>
              <div style={{ width: 260, maxWidth: '72%', borderRadius: 18, background: 'rgba(255,255,255,.86)', padding: 24, color: t.accent, backdropFilter: 'blur(12px)' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 30, lineHeight: 1.04, fontWeight: 400 }}>{featured.title}</h3>
                <p style={{ margin: '0 0 18px', color: t.mutedColor, fontSize: 13, lineHeight: 1.5 }}>{featured.description}</p>
                <WaasButton action={{ label: featured.buttonLabel || 'Read more', url: featured.url || '/services', size: 'small' }} section={section} theme={t} />
              </div>
              <div className="waas-template-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, color: '#ffffff' }}>
                {metrics.slice(0, 3).map((item, index) => (
                  <div key={`${item.label || item.title || index}-metric`}>
                    <span style={{ display: 'block', fontSize: 11, opacity: .82 }}>{item.label || `0${index + 1}`}</span>
                    <strong style={{ display: 'block', marginTop: 4, fontSize: 25, fontWeight: 400 }}>{item.value || item.title || item.icon || `0${index + 1}`}</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

const WaasServiceDetailSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const c = section.content || {};
  const imageUrl = getImageUrl(c);
  const blocks = getBlocks(section);
  const metrics = getBlocks(section, 'metrics').slice(0, 3);
  const highlights = getBlocks(section, 'highlights').slice(0, 4);
  const imageTiles = [
    { ...c, link: c.imageLink || c.link, title: c.imageAlt || c.title, resolvedImageUrl: imageUrl },
    ...blocks.filter((item) => waasFlowImageUrl(item)).map((item) => ({ ...item, title: item.title, resolvedImageUrl: waasFlowImageUrl(item) }))
  ].filter((item) => item.resolvedImageUrl).slice(0, 3);
  const isServerInfrastructure = c.anchorId === 'server-infrastructure'
    || String(c.eyebrow || '').toUpperCase().includes('SERVER & CLOUD');
  const premiumEnabled = isServerInfrastructure || metrics.length > 0 || highlights.length > 0;

  if (premiumEnabled) {
    const heroImage = imageTiles[0]?.resolvedImageUrl || imageUrl;
    const accent = section.style?.accentColor || '#c9a46a';
    const shellBackground = section.style?.panelColor || section.style?.backgroundColor || section.style?.background || t.background || '#0b0f16';
    const shellLight = waasIsLightColor(shellBackground);
    const shellText = shellLight ? '#0b0f16' : '#fffdf8';
    const shellMuted = shellLight ? 'rgba(11,15,22,.66)' : 'rgba(255,253,248,.72)';
    const shellMutedSoft = shellLight ? 'rgba(11,15,22,.6)' : 'rgba(255,253,248,.62)';
    const shellBorder = shellLight ? 'rgba(11,15,22,.14)' : 'rgba(255,255,255,.16)';
    const shellPanel = shellLight ? 'rgba(11,15,22,.05)' : 'rgba(255,255,255,.07)';
    const shellPanelSoft = shellLight ? 'rgba(11,15,22,.035)' : 'rgba(255,255,255,.045)';
    const visualTitle = c.visualTitle || c.title;
    const visualSubtitle = c.visualSubtitle || c.subtitle;
    const highlightBlocks = highlights.length ? highlights : blocks.slice(0, 4);

    return (
      <section style={waasSectionStyle(section, t)}>
        <div style={{ ...waasContainerStyle(section, t), overflow: 'hidden', borderRadius: 30, background: shellBackground, color: shellText, boxShadow: '0 28px 70px rgba(11,15,22,.24)', border: `1px solid ${shellBorder}` }}>
          <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.98fr 1.02fr', minHeight: 560, alignItems: 'stretch' }}>
            <div style={{ padding: 'clamp(34px, 5cqw, 58px)', display: 'flex', flexDirection: 'column', gap: 34, justifyContent: 'space-between' }}>
              <div>
                {c.eyebrow && <p style={{ ...waasFlowEyebrowStyle(section, t), display: 'inline-flex', margin: '0 0 18px', border: `1px solid ${accent}`, borderRadius: 999, padding: '8px 12px', color: accent, background: 'rgba(201,164,106,.08)' }}>{c.eyebrow}</p>}
                <h2 style={{ ...waasFlowHeadingStyle(t, 'clamp(42px, 5.4cqw, 76px)'), color: shellText, maxWidth: 690 }}>{c.title}</h2>
                {c.subtitle && <p style={{ margin: '24px 0 0', maxWidth: 640, color: shellMuted, fontSize: 16, lineHeight: 1.7 }}>{c.subtitle}</p>}
              </div>

              {metrics.length > 0 && (
                <div className="waas-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                  {metrics.map((item, index) => (
                    <article key={`${item.label || item.value}-${index}`} style={{ minHeight: 104, borderRadius: 16, border: `1px solid ${shellBorder}`, background: shellPanel, padding: 18 }}>
                      <strong style={{ display: 'block', color: shellText, fontSize: 24, lineHeight: 1.05, fontWeight: 500 }}>{item.value}</strong>
                      <span style={{ display: 'block', marginTop: 8, color: shellMutedSoft, fontSize: 12, lineHeight: 1.4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em' }}>{item.label}</span>
                    </article>
                  ))}
                </div>
              )}

              <div className="waas-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
                {highlightBlocks.map((item, index) => (
                  <article key={`${item.title}-${index}`} style={{ borderRadius: 18, border: `1px solid ${shellBorder}`, background: shellPanelSoft, padding: 20 }}>
                    <span style={{ display: 'inline-flex', marginBottom: 16, color: accent, fontSize: 11, fontWeight: 900, letterSpacing: '.12em' }}>{item.icon || `0${index + 1}`}</span>
                    <h3 style={{ margin: '0 0 8px', color: shellText, fontSize: 18, lineHeight: 1.2, fontWeight: 800 }}>{item.title}</h3>
                    <p style={{ margin: 0, color: shellMutedSoft, fontSize: 13, lineHeight: 1.55 }}>{item.description}</p>
                  </article>
                ))}
              </div>

              <div>
                <WaasButton action={{ label: c.primaryActionLabel, url: c.primaryActionUrl, size: 'small', color: accent, textColor: '#0b0f16' }} section={section} theme={t} />
              </div>
            </div>

            <div style={{ position: 'relative', minHeight: 560, overflow: 'hidden', background: '#111827' }}>
              {heroImage && (
                <WaasLinkedElement item={imageTiles[0] || c} fallbackUrl={(imageTiles[0] || c).url} style={{ position: 'absolute', inset: 0 }}>
                  <img src={heroImage} alt={c.imageAlt || c.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </WaasLinkedElement>
              )}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(11,15,22,.15), rgba(11,15,22,.82))' }} />
              <div style={{ position: 'relative', zIndex: 1, minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 'clamp(24px, 4cqw, 42px)' }}>
                <div style={{ alignSelf: 'flex-end', borderRadius: 999, border: '1px solid rgba(255,255,255,.22)', background: 'rgba(11,15,22,.48)', color: '#fffdf8', padding: '9px 13px', fontSize: 11, fontWeight: 900, letterSpacing: '.12em', textTransform: 'uppercase' }}>
                  Cloud / Servers / DevOps
                </div>
                <div style={{ maxWidth: 430, borderRadius: 22, background: 'rgba(255,253,248,.9)', color: '#0b0f16', padding: 26, boxShadow: '0 22px 50px rgba(11,15,22,.22)' }}>
                  <p style={{ margin: '0 0 10px', color: accent, fontSize: 11, fontWeight: 900, letterSpacing: '.12em', textTransform: 'uppercase' }}>Operating Layer</p>
                  <h3 style={{ margin: 0, fontSize: 28, lineHeight: 1.05, fontWeight: 500 }}>{visualTitle}</h3>
                  {visualSubtitle && <p style={{ margin: '14px 0 0', color: 'rgba(11,15,22,.68)', fontSize: 14, lineHeight: 1.55 }}>{visualSubtitle}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={{ ...waasContainerStyle(section, t), background: section.style?.backgroundColor || '#d8c2b6', borderRadius: 26, padding: '50px 16px 0 58px', minHeight: 450, overflow: 'hidden', boxShadow: '0 18px 42px rgba(24,34,46,.1)' }}>
        <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.9fr 1fr', gap: 44, alignItems: 'stretch' }}>
          <div style={{ paddingBottom: 48, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              {c.eyebrow && <p style={waasFlowEyebrowStyle(section, t)}>{c.eyebrow}</p>}
              <h2 style={waasFlowHeadingStyle(t, 'clamp(44px, 5.2cqw, 70px)')}>{c.title}</h2>
              {c.subtitle && <p style={{ margin: '24px 0 0', maxWidth: 420, color: 'rgba(11,15,22,.72)', fontSize: 15, lineHeight: 1.55 }}>{c.subtitle}</p>}
            </div>
            <div style={{ borderLeft: `1px solid rgba(11,15,22,.38)`, paddingLeft: 26, display: 'grid', gap: 18, maxWidth: 360 }}>
              {blocks.slice(0, 4).map((item, index) => (
                <a key={`${item.title}-${index}`} {...waasLinkProps(item, item.url || c.primaryActionUrl)} style={{ color: index === 0 ? t.accent : 'rgba(11,15,22,.38)', textDecoration: 'none', fontSize: 14, fontWeight: index === 0 ? 800 : 500 }}>
                  {item.title}
                </a>
              ))}
              <div style={{ marginTop: 4 }}><WaasButton action={{ label: c.primaryActionLabel, url: c.primaryActionUrl, size: 'small' }} section={section} theme={t} /></div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateRows: '1fr 1.35fr 1fr', gap: 12, minHeight: 450 }}>
            {(imageTiles.length ? imageTiles : [{ title: c.title, resolvedImageUrl: imageUrl }]).map((item, index) => (
              <figure key={`${item.title}-${index}`} style={{ margin: 0, borderRadius: '16px 0 0 16px', overflow: 'hidden', minHeight: index === 1 ? 190 : 130, background: '#ffffff88' }}>
                {item.resolvedImageUrl && (
                  <WaasLinkedElement item={item} fallbackUrl={item.url} style={{ height: '100%' }}>
                    <img src={item.resolvedImageUrl} alt={item.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </WaasLinkedElement>
                )}
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WaasStatsStripSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div className="waas-template-grid" style={{ ...waasContainerStyle(section, t), display: 'grid', gridTemplateColumns: '.9fr 1.1fr', gap: 56, alignItems: 'center' }}>
        <div>
          {section.content?.eyebrow && <p style={{ margin: '0 0 16px', color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em' }}>{section.content.eyebrow}</p>}
          <h2 style={{ margin: '0 0 20px', fontFamily: t.headingFontFamily, fontWeight: 500, color: section.style?.color || '#fffdf8', fontSize: 'clamp(38px, 4.4cqw, 62px)', lineHeight: 1.04 }}>{section.content?.title}</h2>
          {section.content?.subtitle && <p style={{ margin: 0, color: '#e7dfd4', fontSize: 18, lineHeight: 1.65 }}>{section.content.subtitle}</p>}
        </div>
        <div className="waas-template-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          {getBlocks(section, 'stats').map((item, index) => (
            <article key={`${item.label}-${index}`} style={{ border: '1px solid rgba(255,253,248,.2)', borderRadius: 18, padding: 22, background: 'rgba(255,253,248,.08)' }}>
              <div style={{ fontFamily: t.headingFontFamily, color: '#fffdf8', fontSize: 40, lineHeight: 1 }}>{item.value}</div>
              <p style={{ margin: '10px 0 0', color: '#e7dfd4', fontSize: 13, lineHeight: 1.45 }}>{item.label}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const WaasProcessStepsSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        {section.content?.eyebrow && <p style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em', margin: '0 0 16px' }}>{section.content.eyebrow}</p>}
        <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.9fr 1.1fr', gap: 54, alignItems: 'start', marginBottom: 42 }}>
          <h2 style={{ margin: 0, fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(42px, 5cqw, 72px)', lineHeight: 1.02 }}>{section.content?.title}</h2>
          <p style={{ margin: 0, color: t.mutedColor, fontSize: 18, lineHeight: 1.7 }}>{section.content?.subtitle}</p>
        </div>
        <div className="waas-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
          {getBlocks(section).map((item, index) => (
            <article key={`${item.title}-${index}`} style={{ background: item.backgroundColor || '#fffdf8', border: `1px solid ${t.borderColor}`, borderRadius: t.cardRadius, padding: 30 }}>
              <WaasLinkedElement item={item} fallbackUrl={item.url}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 42 }}>
                  <span style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.12em' }}>{item.badge || `STEP ${index + 1}`}</span>
                  <span style={{ fontFamily: t.headingFontFamily, fontSize: 42, color: '#d9cfc1' }}>{item.icon || `0${index + 1}`}</span>
                </div>
                <h3 style={{ margin: '0 0 12px', fontFamily: t.headingFontFamily, color: item.color || t.accent, fontSize: 28 }}>{item.title}</h3>
                <p style={{ margin: 0, color: t.mutedColor, lineHeight: 1.65 }}>{item.description}</p>
              </WaasLinkedElement>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const WaasInstructorSpotlightSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const c = section.content || {};
  const imageUrl = getImageUrl(c);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div className="waas-template-grid" style={{ ...waasContainerStyle(section, t), display: 'grid', gridTemplateColumns: '.78fr 1fr', gap: 72, alignItems: 'center' }}>
        <div>{imageUrl && (
          <WaasLinkedElement item={{ ...c, link: c.imageLink || c.link }} fallbackUrl={c.imageLink?.url || ''}>
            <img src={imageUrl} alt={c.imageAlt || ''} style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', borderRadius: t.imageRadius, boxShadow: t.cardShadow }} />
          </WaasLinkedElement>
        )}</div>
        <div>
          {c.eyebrow && <p style={{ margin: '0 0 18px', color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em' }}>{c.eyebrow}</p>}
          <h2 style={{ margin: '0 0 24px', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(42px, 4.8cqw, 66px)', lineHeight: 1.03 }}>{c.title}</h2>
          <p style={{ margin: '0 0 32px', color: t.mutedColor, fontSize: 18, lineHeight: 1.72 }}>{c.subtitle}</p>
          <div style={{ display: 'grid', gap: 12, marginBottom: 28 }}>
            {getBlocks(section).map((item, index) => (
              <div key={`${item.title}-${index}`} style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 14, alignItems: 'start' }}>
                <span style={{ display: 'grid', placeItems: 'center', width: 44, height: 44, borderRadius: 999, background: t.secondary, color: t.accent, fontWeight: 800 }}>{item.icon || index + 1}</span>
                <div><strong style={{ color: t.accent }}>{item.title}</strong><p style={{ margin: '4px 0 0', color: t.mutedColor }}>{item.description}</p></div>
              </div>
            ))}
          </div>
          <WaasButton action={{ label: c.primaryActionLabel, url: c.primaryActionUrl, variant: 'outline' }} section={section} theme={t} />
        </div>
      </div>
    </section>
  );
};

const WaasScheduleSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        {section.content?.eyebrow && <p style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em', margin: '0 0 16px' }}>{section.content.eyebrow}</p>}
        <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.8fr 1.2fr', gap: 56, alignItems: 'start' }}>
          <div>
            <h2 style={{ margin: '0 0 20px', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(42px, 4.8cqw, 66px)', lineHeight: 1.04 }}>{section.content?.title}</h2>
            <p style={{ color: t.mutedColor, lineHeight: 1.7 }}>{section.content?.subtitle}</p>
            {section.content?.timezoneLabel && <p style={{ color: section.style?.accentColor || t.primary, fontSize: 12, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{section.content.timezoneLabel}</p>}
            <WaasButton action={{ label: section.content?.primaryActionLabel, url: section.content?.primaryActionUrl }} section={section} theme={t} />
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            {getBlocks(section, 'scheduleOptions').map((item, index) => (
              <article key={`${item.title}-${index}`} className="waas-template-schedule-row" style={{ display: 'grid', gridTemplateColumns: '88px minmax(0, 1fr) auto', gap: 18, alignItems: 'center', background: '#fffdf8', border: `1px solid ${t.borderColor}`, borderRadius: 12, padding: 18 }}>
                <div style={{ textAlign: 'center', color: t.accent }}><strong>{item.date}</strong><div style={{ fontSize: 12 }}>{item.time}</div></div>
                <div><h3 style={{ margin: 0, fontFamily: t.headingFontFamily, color: t.accent }}>{item.title}</h3><p style={{ margin: '4px 0 0', color: t.mutedColor }}>{item.description}</p></div>
                <WaasButton action={{ label: item.buttonLabel || item.badge, url: item.url, variant: 'outline', size: 'small' }} section={section} theme={t} />
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WaasPricingPlansSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 46px' }}>
          {section.content?.eyebrow && <p style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em', margin: '0 0 16px' }}>{section.content.eyebrow}</p>}
          <h2 style={{ margin: '0 0 16px', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(42px, 4.8cqw, 68px)', lineHeight: 1.03 }}>{section.content?.title}</h2>
          <p style={{ margin: 0, color: t.mutedColor, fontSize: 18, lineHeight: 1.7 }}>{section.content?.subtitle}</p>
        </div>
        <div className="waas-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          {getBlocks(section).map((item, index) => (
            <article key={`${item.title}-${index}`} style={{ background: item.backgroundColor || '#fffdf8', border: `1px solid ${t.borderColor}`, borderRadius: 12, padding: 32, boxShadow: t.cardShadow }}>
              {item.badge && <span style={{ display: 'inline-flex', marginBottom: 22, color: section.style?.accentColor || t.primary, fontSize: 12, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{item.badge}</span>}
              <h3 style={{ margin: '0 0 12px', fontFamily: t.headingFontFamily, color: item.color || t.accent, fontSize: 32 }}>{item.title}</h3>
              <p style={{ color: t.mutedColor }}>{item.description}</p>
              <div style={{ margin: '26px 0', color: t.accent }}><span style={{ fontFamily: t.headingFontFamily, fontSize: 52 }}>{item.price}</span><span style={{ color: t.mutedColor }}> {item.period}</span></div>
              <ul style={{ display: 'grid', gap: 10, padding: 0, listStyle: 'none', color: t.mutedColor }}>{(Array.isArray(item.features) ? item.features : []).map((feature) => <li key={feature}>- {feature}</li>)}</ul>
              <div style={{ marginTop: 28 }}><WaasButton action={{ label: item.buttonLabel, url: item.url, variant: index === 1 ? 'primary' : 'outline' }} section={section} theme={t} /></div>
            </article>
          ))}
        </div>
        {section.content?.disclaimer && <p style={{ margin: '24px 0 0', textAlign: 'center', color: t.mutedColor, fontSize: 13 }}>{section.content.disclaimer}</p>}
      </div>
    </section>
  );
};

const WaasTestimonialsSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const blocks = getBlocks(section);
  const featured = blocks[0] || {};
  const positions = [
    { left: '50%', width: 224, height: 276, rotate: 0, zIndex: 4, opacity: 1 },
    { left: '25%', width: 134, height: 166, rotate: -7, zIndex: 2, opacity: .9 },
    { left: '75%', width: 134, height: 166, rotate: 8, zIndex: 2, opacity: .9 },
    { left: '3%', width: 122, height: 152, rotate: -10, zIndex: 1, opacity: .72 },
    { left: '97%', width: 122, height: 152, rotate: 9, zIndex: 1, opacity: .72 }
  ];
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={{ ...waasContainerStyle(section, t), textAlign: 'center' }}>
        <h2 style={{ ...waasFlowHeadingStyle(t, 'clamp(44px, 5.6cqw, 72px)'), maxWidth: 760, margin: '0 auto 34px' }}>{section.content?.title}</h2>
        <div className="waas-template-testimonial-mosaic" style={{ position: 'relative', minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 34 }}>
          {blocks.map((item, index) => {
            const imageUrl = waasFlowImageUrl(item);
            const isFeatured = index === 0;
            const pos = positions[index] || positions[positions.length - 1];
            return (
              <figure key={`${item.title}-${index}`} style={{ margin: 0, position: 'absolute', left: pos.left, top: isFeatured ? 0 : 70, transform: `translateX(-50%) rotate(${pos.rotate}deg)`, zIndex: pos.zIndex, width: pos.width, height: pos.height, borderRadius: 22, overflow: 'hidden', border: isFeatured ? '8px solid rgba(255,255,255,.92)' : '0', boxShadow: isFeatured ? '0 28px 70px rgba(24,34,46,.22), 0 0 82px rgba(94,198,213,.32)' : '0 18px 42px rgba(24,34,46,.14)', opacity: pos.opacity }}>
                {imageUrl ? (
                  <WaasLinkedElement item={item} fallbackUrl={item.url} style={{ height: '100%' }}>
                    <img src={imageUrl} alt={item.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </WaasLinkedElement>
                ) : <div style={{ width: '100%', height: '100%', background: '#ffffff' }} />}
                {isFeatured && (
                  <figcaption style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '48px 18px 18px', color: '#ffffff', background: 'linear-gradient(180deg, transparent, rgba(11,15,22,.66))' }}>
                    <strong>{item.title}</strong>
                    <span style={{ display: 'block', fontSize: 11 }}>{item.description}</span>
                  </figcaption>
                )}
              </figure>
            );
          })}
          <span style={{ position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)', width: 58, height: 58, display: 'grid', placeItems: 'center', borderRadius: 999, background: '#ffffff', boxShadow: '0 14px 28px rgba(24,34,46,.16)', fontSize: 35, color: t.accent }}>"</span>
        </div>
        <h3 style={{ margin: '0 0 12px', color: t.accent, fontSize: 24, lineHeight: 1.2, fontWeight: 600 }}>{featured.quote || featured.description}</h3>
        {section.content?.subtitle && <p style={{ maxWidth: 620, margin: '0 auto', color: t.mutedColor, lineHeight: 1.65, fontSize: 14 }}>{section.content.subtitle}</p>}
      </div>
    </section>
  );
};

const WaasFaqSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div className="waas-template-grid" style={{ ...waasContainerStyle(section, t), display: 'grid', gridTemplateColumns: '.8fr 1.2fr', gap: 64, alignItems: 'start' }}>
        <div>
          {section.content?.eyebrow && <p style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em', margin: '0 0 16px' }}>{section.content.eyebrow}</p>}
          <h2 style={{ margin: '0 0 18px', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(42px, 4.8cqw, 66px)', lineHeight: 1.04 }}>{section.content?.title}</h2>
          <p style={{ color: t.mutedColor, lineHeight: 1.7 }}>{section.content?.subtitle}</p>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          {getBlocks(section).map((item, index) => (
            <details key={`${item.title}-${index}`} open={index === 0} style={{ background: '#fffdf8', border: `1px solid ${t.borderColor}`, borderRadius: 10, padding: 20 }}>
              <summary style={{ cursor: 'pointer', color: t.accent, fontWeight: 800 }}>{item.title}</summary>
              <p style={{ color: t.mutedColor, lineHeight: 1.7 }}>{item.description}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

const WaasImageGallerySection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const blocks = getBlocks(section);
  const featured = blocks[0] || {};
  const imageUrl = waasFlowImageUrl(featured) || waasFlowImageUrl(section.content || {});
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.95fr 1.05fr', gap: 56, alignItems: 'center' }}>
          <div>
            {section.content?.eyebrow && <p style={waasFlowEyebrowStyle(section, t)}>{section.content.eyebrow}</p>}
            <h2 style={waasFlowHeadingStyle(t, 'clamp(44px, 5.2cqw, 68px)')}>{section.content?.title}</h2>
            {section.content?.subtitle && <p style={{ maxWidth: 430, margin: '28px 0 34px', color: t.mutedColor, fontSize: 15, lineHeight: 1.58 }}>{section.content.subtitle}</p>}
            <div style={{ display: 'flex', gap: 16 }}>
              <button type="button" aria-label="Previous insight" style={{ width: 54, height: 54, border: 0, borderRadius: 999, background: '#ffffff', boxShadow: '0 16px 30px rgba(24,34,46,.08)', color: t.accent, fontSize: 24 }}>{'<'}</button>
              <button type="button" aria-label="Next insight" style={{ width: 54, height: 54, border: 0, borderRadius: 999, background: '#ffffff', boxShadow: '0 16px 30px rgba(24,34,46,.08)', color: t.accent, fontSize: 24 }}>{'>'}</button>
            </div>
          </div>
          <div style={{ position: 'relative', minHeight: 400 }}>
            <div style={{ position: 'absolute', inset: '30px 24px 26px 68px', borderRadius: 26, background: '#b8c3da', transform: 'rotate(0deg)', opacity: .85 }} />
            <article style={{ position: 'relative', width: 'min(370px, 100%)', minHeight: 398, margin: '0 auto', border: '8px solid rgba(255,255,255,.92)', borderRadius: 26, overflow: 'hidden', boxShadow: '0 28px 62px rgba(24,34,46,.22)', background: '#ffffff' }}>
              {imageUrl && (
                <WaasLinkedElement item={featured} fallbackUrl={featured.url} style={{ position: 'absolute', inset: 0 }}>
                  <img src={imageUrl} alt={featured.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </WaasLinkedElement>
              )}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(11,15,22,.02), rgba(11,15,22,.68))' }} />
              <div style={{ position: 'absolute', left: 24, right: 24, bottom: 24, color: '#ffffff' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <span style={{ borderRadius: 999, background: '#ffffff', color: t.accent, padding: '6px 12px', fontSize: 11, fontWeight: 800 }}>{featured.badge || 'Article'}</span>
                  <span style={{ borderRadius: 999, background: '#ffffff', color: t.accent, padding: '6px 12px', fontSize: 11, fontWeight: 800 }}>IT</span>
                </div>
                <h3 style={{ margin: '0 0 10px', fontSize: 30, lineHeight: 1.05, fontWeight: 500 }}>{featured.title}</h3>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>{featured.description}</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

const WaasCareerHeroSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const c = section.content || {};
  const imageUrl = getImageUrl(c);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div className={imageUrl ? 'waas-template-grid' : ''} style={{ ...waasContainerStyle(section, t), display: imageUrl ? 'grid' : 'block', gridTemplateColumns: '1fr .72fr', gap: 48, alignItems: 'center', textAlign: imageUrl ? 'left' : 'center', maxWidth: imageUrl ? section.style?.maxWidth || t.pageWidth : 860 }}>
        <div>
          {c.eyebrow && <p style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em', margin: '0 0 18px' }}>{c.eyebrow}</p>}
          <h1 style={{ margin: '0 0 20px', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(42px, 5.8cqw, 72px)', lineHeight: .98 }}>{c.title}</h1>
          <p style={{ maxWidth: 680, margin: imageUrl ? '0 0 28px' : '0 auto 28px', color: t.mutedColor, fontSize: 17, lineHeight: 1.6 }}>{c.subtitle}</p>
          <div style={{ display: 'flex', justifyContent: imageUrl ? 'flex-start' : 'center', flexWrap: 'wrap', gap: 14 }}>{getActions(section).map((action, index) => <WaasButton key={`${action.label}-${index}`} action={action} section={section} theme={t} />)}</div>
        </div>
        {imageUrl && (
          <WaasLinkedElement item={{ ...c, link: c.imageLink || c.link }} fallbackUrl={c.imageLink?.url || ''}>
            <img src={imageUrl} alt={c.imageAlt || ''} style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', borderRadius: t.imageRadius, boxShadow: t.cardShadow }} />
          </WaasLinkedElement>
        )}
      </div>
    </section>
  );
};

const WaasOpenRolesSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const companyState = useSelector((state) => state.companyReducer?.company || state.company?.companyDetails || {});
  const companyId = companyState?.primaryId;
  const { careers, loading } = useCareerOpenings(companyId);
  const [selectedRole, setSelectedRole] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState({ type: '', text: '' });
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [applicationForm, setApplicationForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    currentLocation: '',
    linkedInUrl: '',
    portfolioUrl: '',
    noticePeriod: '',
    expectedCompensation: '',
    whyWorkWithUs: ''
  });

  useEffect(() => {
    if (!snackbarMessage) {
      return undefined;
    }
    const timer = window.setTimeout(() => setSnackbarMessage(''), 4000);
    return () => window.clearTimeout(timer);
  }, [snackbarMessage]);

  const dynamicRoles = careers.map((career) => ({
    id: career.primaryId,
    title: career.title,
    badge: career.department || (career.primaryId ? `ID ${career.primaryId}` : ''),
    description: getCareerDescription(career) || career.fullDescription,
    location: career.location,
    career,
    skills: Array.isArray(career.skills)
      ? career.skills
      : typeof career.skills === 'string'
        ? career.skills.split(',').map((skill) => skill.trim()).filter(Boolean)
        : [],
    applyHref: careerApplyHref(career),
    external: Boolean(career.applicationUrl),
  }));
  const staticRoles = getBlocks(section).map((item, index) => ({
    ...item,
    id: item.id || `${item.title || 'role'}-${index}`,
    applyHref: item.route || item.url || '/careers#career-application',
    skills: Array.isArray(item.skills)
      ? item.skills
      : typeof item.skills === 'string'
        ? item.skills.split(',').map((skill) => skill.trim()).filter(Boolean)
        : [],
  }));
  const roles = dynamicRoles.length > 0 ? dynamicRoles : staticRoles;
  const skeletonRoles = loading && roles.length === 0 ? Array.from({ length: 3 }, (_, index) => ({ id: `loading-${index}`, loading: true })) : [];
  const visibleRoles = skeletonRoles.length > 0 ? skeletonRoles : roles;
  const handleApplicationFieldChange = (event) => {
    const { name, value } = event.target;
    setApplicationForm((current) => ({ ...current, [name]: value }));
  };

  const handleRoleApply = (role) => {
    if (role?.career) {
      setSelectedRole(role);
      setApplicationMessage({ type: '', text: '' });
      setApplicationDialogOpen(true);
    }
  };

  const handleApplicationSubmit = async (event) => {
    event.preventDefault();

    if (!companyId || !selectedRole?.career?.primaryId) {
      setApplicationMessage({ type: 'error', text: 'Please choose a role before submitting.' });
      return;
    }

    if (!resumeFile) {
      setApplicationMessage({ type: 'error', text: 'Please upload your resume before submitting.' });
      return;
    }

    setSubmitting(true);
    setApplicationMessage({ type: '', text: '' });

    try {
      await applyForCareer({
        companyId,
        careerId: selectedRole.career.primaryId,
        firstName: applicationForm.firstName.trim(),
        lastName: applicationForm.lastName.trim(),
        email: applicationForm.email.trim(),
        phoneNumber: applicationForm.phoneNumber.trim(),
        currentLocation: applicationForm.currentLocation.trim(),
        linkedInUrl: applicationForm.linkedInUrl.trim(),
        portfolioUrl: applicationForm.portfolioUrl.trim(),
        noticePeriod: applicationForm.noticePeriod.trim(),
        createdBy: `${applicationForm.firstName.trim()} ${applicationForm.lastName.trim()}`,
        expectedCompensation: applicationForm.expectedCompensation.trim(),
        whyWorkWithUs: applicationForm.whyWorkWithUs.trim()
      }, resumeFile);

      setApplicationForm({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        currentLocation: '',
        linkedInUrl: '',
        portfolioUrl: '',
        noticePeriod: '',
        expectedCompensation: '',
        whyWorkWithUs: ''
      });
      setResumeFile(null);
      setApplicationDialogOpen(false);
      setSnackbarMessage('Applied successfully.');
    } catch {
      setApplicationMessage({ type: 'error', text: 'We could not submit your application. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    ...waasFormInput(t),
    width: '100%',
    boxSizing: 'border-box'
  };

  const closeApplicationDialog = () => {
    if (!submitting) {
      setApplicationDialogOpen(false);
    }
  };

  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={waasContainerStyle(section, t)}>
        {section.content?.eyebrow && <p style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.22em', margin: '0 0 18px' }}>{section.content.eyebrow}</p>}
        <h2 style={{ margin: '0 0 12px', fontFamily: t.headingFontFamily, fontWeight: 500, color: t.accent, fontSize: 'clamp(40px, 4.8cqw, 66px)' }}>{section.content?.title}</h2>
        <p style={{ maxWidth: 680, margin: '0 0 38px', color: t.mutedColor, fontSize: 18 }}>{section.content?.subtitle}</p>
        <div className="waas-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
          {visibleRoles.map((item) => (
            <article key={item.id} style={{ background: '#fffdf8', border: `1px solid ${t.borderColor}`, borderRadius: 8, padding: 28, minHeight: 300, display: 'grid', alignContent: 'start', gap: 18 }}>
              {item.loading ? (
                <>
                  <span style={{ width: 92, height: 12, borderRadius: 99, background: '#eadfcb' }} />
                  <span style={{ width: '82%', height: 30, borderRadius: 6, background: '#eadfcb' }} />
                  <span style={{ width: '100%', height: 68, borderRadius: 6, background: '#f0e7d6' }} />
                  <span style={{ width: 132, height: 42, borderRadius: 6, background: '#eadfcb' }} />
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    {item.badge && <span style={{ display: 'inline-flex', color: section.style?.accentColor || t.primary, fontSize: 12, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{item.badge}</span>}
                    {item.career?.primaryId && <span style={{ color: t.mutedColor, fontSize: 12, fontWeight: 800 }}>ID {item.career.primaryId}</span>}
                  </div>
                  <h3 style={{ margin: 0, fontFamily: t.headingFontFamily, fontSize: 28, lineHeight: 1.12, color: t.accent }}>{item.title}</h3>
                  <p style={{ margin: 0, color: t.mutedColor, lineHeight: 1.65 }}>{item.description}</p>
                  {item.career ? (
                    <div style={{ display: 'grid', gap: 10, color: t.mutedColor, fontSize: 14 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><BriefcaseBusiness size={15} />{getEmploymentLabel(item.career.employmentType)} | {getWorkModeLabel(item.career.workMode)}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><MapPin size={15} />{item.location || 'Flexible'}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><CalendarDays size={15} />Closes {formatCareerDate(item.career.closingDate)}</span>
                    </div>
                  ) : (
                    item.location && <strong style={{ color: t.accent }}>{item.location}</strong>
                  )}
                  {item.skills?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {item.skills.slice(0, 4).map((skill) => (
                        <span key={skill} style={{ border: `1px solid ${t.borderColor}`, borderRadius: 999, padding: '4px 10px', color: t.mutedColor, fontSize: 12 }}>{skill}</span>
                      ))}
                    </div>
                  )}
                  {item.external ? (
                    <a
                      href={item.applyHref}
                      target="_blank"
                      rel="noreferrer"
                      style={{ width: 'fit-content', minHeight: 42, display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center', padding: '0 16px', borderRadius: 6, background: t.accent, color: '#fffdf8', textDecoration: 'none', fontWeight: 800, marginTop: 'auto' }}
                    >
                      Apply <ArrowRight size={15} />
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRoleApply(item)}
                      style={{ width: 'fit-content', minHeight: 42, display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center', padding: '0 16px', border: 0, borderRadius: 6, background: t.accent, color: '#fffdf8', textDecoration: 'none', fontWeight: 800, marginTop: 'auto', cursor: 'pointer' }}
                    >
                      Apply <ArrowRight size={15} />
                    </button>
                  )}
                </>
              )}
            </article>
          ))}
        </div>
        {!loading && visibleRoles.length === 0 && (
          <div style={{ border: `1px solid ${t.borderColor}`, borderRadius: 8, padding: 28, background: '#fffdf8', color: t.mutedColor }}>
            No open roles are published right now. Check back soon or send a general application from the careers page.
          </div>
        )}
        {applicationDialogOpen && (
          <div
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.target === event.currentTarget && closeApplicationDialog()}
            style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(15, 23, 42, .48)', display: 'grid', placeItems: 'center', padding: 18 }}
          >
            <form id="career-application" onSubmit={handleApplicationSubmit} style={{ width: 'min(820px, 100%)', maxHeight: '90vh', overflow: 'auto', border: `1px solid ${t.borderColor}`, borderRadius: 8, padding: 28, background: '#fffdf8', display: 'grid', gap: 18, boxShadow: '0 24px 70px rgba(15, 23, 42, .24)' }}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <p style={{ color: section.style?.accentColor || t.primary, fontWeight: 800, letterSpacing: '.16em', margin: '0 0 10px', textTransform: 'uppercase' }}>Application</p>
                  <h3 style={{ margin: 0, fontFamily: t.headingFontFamily, fontSize: 34, lineHeight: 1.12, color: t.accent }}>
                    {selectedRole?.title ? `Apply for ${selectedRole.title}` : 'Select a role to apply'}
                  </h3>
                </div>
                <button type="button" onClick={closeApplicationDialog} disabled={submitting} style={{ border: 0, background: 'transparent', color: t.accent, fontSize: 24, cursor: submitting ? 'wait' : 'pointer' }}>x</button>
              </div>
            {applicationMessage.text && (
              <div style={{ borderRadius: 6, padding: '12px 14px', background: applicationMessage.type === 'success' ? '#e8f7ee' : '#fdecec', color: applicationMessage.type === 'success' ? '#17663a' : '#9b1c1c', fontWeight: 700 }}>
                {applicationMessage.text}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <input name="firstName" value={applicationForm.firstName} onChange={handleApplicationFieldChange} placeholder="First name" required style={inputStyle} />
              <input name="lastName" value={applicationForm.lastName} onChange={handleApplicationFieldChange} placeholder="Last name" required style={inputStyle} />
              <input name="email" value={applicationForm.email} onChange={handleApplicationFieldChange} placeholder="Email" type="email" required style={inputStyle} />
              <input name="phoneNumber" value={applicationForm.phoneNumber} onChange={handleApplicationFieldChange} placeholder="Phone number" required style={inputStyle} />
              <input name="currentLocation" value={applicationForm.currentLocation} onChange={handleApplicationFieldChange} placeholder="Current location" style={inputStyle} />
              <input name="noticePeriod" value={applicationForm.noticePeriod} onChange={handleApplicationFieldChange} placeholder="Notice period" style={inputStyle} />
              <input name="linkedInUrl" value={applicationForm.linkedInUrl} onChange={handleApplicationFieldChange} placeholder="LinkedIn URL" type="url" style={inputStyle} />
              <input name="portfolioUrl" value={applicationForm.portfolioUrl} onChange={handleApplicationFieldChange} placeholder="Portfolio URL" type="url" style={inputStyle} />
            </div>
            <input name="expectedCompensation" value={applicationForm.expectedCompensation} onChange={handleApplicationFieldChange} placeholder="Expected compensation" style={inputStyle} />
            <textarea name="whyWorkWithUs" value={applicationForm.whyWorkWithUs} onChange={handleApplicationFieldChange} placeholder="Why do you want to work with us?" rows={4} style={{ ...inputStyle, height: 'auto', paddingTop: 12, resize: 'vertical' }} />
            <label style={{ display: 'grid', gap: 8, color: t.accent, fontWeight: 800 }}>
              Resume
              <input
                type="file"
                accept=".pdf,.doc,.docx,.rtf"
                onChange={(event) => setResumeFile(event.target.files?.[0] || null)}
                required
                style={{ ...inputStyle, paddingTop: 8 }}
              />
              <span style={{ color: t.mutedColor, fontWeight: 500, fontSize: 13 }}>{resumeFile?.name || 'Accepted formats: PDF, DOC, DOCX, RTF'}</span>
            </label>
            <button
              type="submit"
              disabled={submitting}
              style={{ width: 'fit-content', minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center', padding: '0 18px', border: 0, borderRadius: 6, background: t.accent, color: '#fffdf8', fontWeight: 800, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? .72 : 1 }}
            >
              {submitting ? 'Submitting...' : 'Submit Application'} <ArrowRight size={15} />
            </button>
            </form>
          </div>
        )}
        {snackbarMessage && (
          <div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 1400, borderRadius: 6, padding: '12px 18px', background: '#17663a', color: '#fff', fontWeight: 800, boxShadow: '0 12px 30px rgba(15, 23, 42, .22)' }}>
            {snackbarMessage}
            <button type="button" onClick={() => setSnackbarMessage('')} style={{ marginLeft: 14, border: 0, background: 'transparent', color: '#fff', fontWeight: 900, cursor: 'pointer' }}>x</button>
          </div>
        )}
      </div>
    </section>
  );
};

const waasFormInput = (theme) => ({
  height: 40,
  border: `1px solid ${theme.borderColor}`,
  borderRadius: theme.inputRadius,
  background: '#ffffff',
  outline: 0,
  padding: '0 12px'
});

const WaasContactSection = ({ section, theme, template }) => {
  const companyState = useSelector((state) => state.companyReducer?.company || state.company?.companyDetails || {});
  const t = waasTheme(theme);
  const content = section.content || {};
  const contactInfo = useTemplateContactInfo(content);
  const c = { ...content, phoneValue: contactInfo.phoneValue, emailValue: contactInfo.emailValue, addressValue: contactInfo.addressValue };
  const fields = getBlocks(section, 'fields');
  const socialLinks = normalizeWaasSocialLinks(c.socialLinks, { ...(template?.company || {}), ...(companyState || {}) });
  const inquiryForm = useTemplateInquiryForm(fields);
  const renderField = (field, index) => {
    const isTextarea = field.type === 'textarea';
    const InputTag = isTextarea ? 'textarea' : 'input';
    const fieldName = inquiryForm.fieldNameFromLabel(field.label, field.type);
    return (
      <label key={`${field.label}-${index}`} style={{ display: 'grid', gap: 8, fontWeight: 700, fontSize: 12 }}>
        {field.label}
        <InputTag
          rows={isTextarea ? 5 : undefined}
          type={isTextarea ? undefined : field.type || 'text'}
          value={inquiryForm.values[fieldName] || ''}
          onChange={(event) => inquiryForm.setFieldValue(fieldName, event.target.value)}
          style={isTextarea ? { ...waasFormInput(t), height: 124, paddingTop: 12 } : waasFormInput(t)}
        />
        {inquiryForm.errors[fieldName] && <span style={{ color: '#b91c1c', fontSize: 12 }}>{inquiryForm.errors[fieldName]}</span>}
      </label>
    );
  };
  const compactFields = fields.slice(0, 2);
  const remainingFields = fields.slice(2);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={{ ...waasContainerStyle(section, t), background: '#ffffff', borderRadius: 24, padding: '40px', boxShadow: t.cardShadow }}>
      <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.9fr .8fr', gap: 58, alignItems: 'start' }}>
        <div>
          {c.eyebrow && <p style={waasFlowEyebrowStyle(section, t)}>{c.eyebrow}</p>}
          <h1 style={waasFlowHeadingStyle(t, 'clamp(40px, 4.8cqw, 60px)')}>{c.title}</h1>
          <p style={{ maxWidth: 520, margin: '22px 0 34px', color: t.mutedColor, fontSize: 16, lineHeight: 1.58 }}>{c.subtitle}</p>
          {[
            { icon: 'P', title: c.phoneTitle, value: c.phoneValue, meta: c.phoneHours },
            { icon: 'E', title: c.emailTitle, value: c.emailValue },
            { icon: 'S', title: c.addressTitle, value: c.addressValue }
          ].map((item) => (
            <div key={item.title} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 16, marginBottom: 24 }}>
              <span style={{ width: 34, height: 34, borderRadius: 999, background: '#f4f5f6', display: 'grid', placeItems: 'center', color: t.accent, fontWeight: 800 }}>{item.icon}</span>
              <div>
                <h3 style={{ margin: '0 0 4px', color: t.accent, fontSize: 17 }}>{item.title}</h3>
                <p style={{ margin: 0, color: t.mutedColor, whiteSpace: 'pre-line' }}>{item.value}</p>
                {item.meta && <p style={{ margin: '4px 0 0', color: t.mutedColor }}>{item.meta}</p>}
              </div>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${t.borderColor}`, display: 'flex', gap: 12, paddingTop: 22, marginTop: 28 }}>
            {socialLinks.map((item) => (
              <a key={`${item.label}-${item.route}`} {...waasLinkProps(item, item.route)} title={item.ariaLabel || item.label} aria-label={item.ariaLabel || item.label} style={{ width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: 999, background: '#f4f5f6', color: t.accent, textDecoration: 'none', fontWeight: 800 }}>
                <WaasSocialIcon item={item} size={14} />
              </a>
            ))}
          </div>
        </div>
        <form style={{ display: 'grid', gap: 18, background: '#f4f5f6', borderRadius: 24, padding: 28 }} onSubmit={inquiryForm.submitInquiry}>
          <h2 style={{ margin: '0 0 8px', color: t.accent, fontSize: 30, fontWeight: 500 }}>{c.formTitle}</h2>
          {compactFields.length > 0 && (
            <div className="waas-template-form-inline" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              {compactFields.map(renderField)}
            </div>
          )}
          {remainingFields.map(renderField)}
          {inquiryForm.status && <p style={{ margin: 0, color: inquiryForm.status.type === 'success' ? '#166534' : '#b91c1c', fontWeight: 700 }}>{inquiryForm.status.message}</p>}
          <button type="submit" disabled={inquiryForm.submitting} style={{ height: 46, border: 0, borderRadius: 999, background: t.accent, color: '#ffffff', fontWeight: 800, letterSpacing: '.04em', opacity: inquiryForm.submitting ? .72 : 1 }}>{inquiryForm.submitting ? 'SENDING...' : (c.submitLabel || 'SUBMIT MESSAGE')}</button>
        </form>
      </div>
      </div>
    </section>
  );
};

const WaasAuthSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const c = section.content || {};
  const imageUrl = getImageUrl(c);
  const componentKey = c.componentKey || section.bindings?.componentKey || (String(c.actionUrl || '').includes('sign-up') ? 'Signup' : 'Login');
  const AuthComponent = componentKey === 'Signup' ? Signup : Login;
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={{ ...waasContainerStyle(section, t), background: '#ffffff', borderRadius: 24, padding: '40px', boxShadow: t.cardShadow }}>
      <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.9fr .78fr', gap: 54, alignItems: 'center' }}>
        <div>
          {c.eyebrow && <p style={waasFlowEyebrowStyle(section, t)}>{c.eyebrow}</p>}
          <h1 style={waasFlowHeadingStyle(t, 'clamp(38px, 4.6cqw, 58px)')}>{c.title}</h1>
          {c.subtitle && <p style={{ maxWidth: 520, margin: '22px 0 26px', color: t.mutedColor, fontSize: 16, lineHeight: 1.58 }}>{c.subtitle}</p>}
          <div style={{ display: 'grid', gap: 14 }}>
            {getBlocks(section).map((item, index) => (
              <div key={`${item.title}-${index}`} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 14, alignItems: 'start' }}>
                <span style={{ width: 34, height: 34, display: 'grid', placeItems: 'center', borderRadius: 999, background: '#f4f5f6', color: t.accent, fontWeight: 800 }}>{item.icon || String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3 style={{ margin: '0 0 4px', color: t.accent, fontSize: 18 }}>{item.title}</h3>
                  {item.description && <p style={{ margin: 0, color: t.mutedColor, lineHeight: 1.55 }}>{item.description}</p>}
                </div>
              </div>
            ))}
          </div>
          {imageUrl && (
            <WaasLinkedElement item={{ ...c, link: c.imageLink || c.link }} fallbackUrl={c.imageLink?.url || ''}>
              <img src={imageUrl} alt={c.imageAlt || ''} style={{ width: '100%', maxHeight: 250, objectFit: 'cover', borderRadius: 22, marginTop: 30 }} />
            </WaasLinkedElement>
          )}
        </div>
        <div className="waas-template-auth-shell" style={{ minWidth: 0, width: '100%', maxWidth: '100%', background: '#f4f5f6', border: `1px solid ${t.borderColor}`, borderRadius: 24, overflow: 'hidden' }}>
          <AuthComponent />
        </div>
      </div>
      </div>
    </section>
  );
};

const WaasNewsletterFooterSection = ({ section, theme }) => {
  const t = waasTheme(theme);
  const c = section.content || {};
  const imageUrl = getImageUrl(c);
  return (
    <section style={waasSectionStyle(section, t)}>
      <div style={{ ...waasContainerStyle(section, t), borderRadius: 28, minHeight: 230, position: 'relative', overflow: 'hidden', boxShadow: '0 22px 54px rgba(24,34,46,.1)', background: '#ffffff' }}>
        {imageUrl && (
          <WaasLinkedElement item={{ ...c, link: c.imageLink || c.link }} fallbackUrl={c.imageLink?.url || ''} style={{ position: 'absolute', inset: 0 }}>
            <img src={imageUrl} alt={c.imageAlt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: .42 }} />
          </WaasLinkedElement>
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(255,255,255,.98), rgba(255,255,255,.82), rgba(255,255,255,.26))' }} />
        <div style={{ position: 'relative', minHeight: 230, display: 'grid', placeItems: 'center', textAlign: 'center', padding: '44px 30px' }}>
          <div>
            <h2 style={{ ...waasFlowHeadingStyle(t, 'clamp(32px, 4cqw, 50px)'), lineHeight: 1.08 }}>{c.title}</h2>
            {c.subtitle && <p style={{ margin: '14px auto 26px', maxWidth: 660, color: t.mutedColor, fontSize: 14, lineHeight: 1.55 }}>{c.subtitle}</p>}
            <WaasButton action={{ label: c.buttonLabel || 'START YOUR PROJECT', url: c.buttonUrl || c.primaryActionUrl || '/contactus' }} section={section} theme={t} />
          </div>
        </div>
      </div>
    </section>
  );
};

const waasResponsiveCss = `
  [data-template-id] .waas-template-grid,
  [data-template-id] .waas-template-card-grid,
  [data-template-id] .waas-template-stat-grid,
  [data-template-id] .waas-template-benefit-grid,
  [data-template-id] .waas-template-auth-shell {
    min-width: 0;
  }
  [data-template-id] .waas-template-auth-shell {
    width: 100%;
    max-width: 100%;
  }
  [data-template-id] .waas-template-auth-shell > div[class*="min-h-screen"] {
    min-height: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    margin-top: 0 !important;
    padding: clamp(18px, 4cqw, 32px) !important;
    background: transparent !important;
    box-sizing: border-box !important;
  }
  [data-template-id] .waas-template-auth-shell > div[class*="min-h-screen"] > div[class*="max-w-7xl"] {
    width: 100% !important;
    max-width: 100% !important;
    grid-template-columns: minmax(0, 1fr) !important;
    box-shadow: none !important;
  }
  [data-template-id] .waas-template-auth-shell > div[class*="min-h-screen"] > div[class*="max-w-7xl"] > div:first-child {
    display: none !important;
  }
  [data-template-id] .waas-template-auth-shell > .max-w-md {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    box-shadow: none !important;
  }
  [data-template-id] .waas-template-auth-shell form,
  [data-template-id] .waas-template-auth-shell form > div,
  [data-template-id] .waas-template-auth-shell input,
  [data-template-id] .waas-template-auth-shell select,
  [data-template-id] .waas-template-auth-shell textarea,
  [data-template-id] .waas-template-auth-shell button {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }
  [data-template-id] .waas-template-auth-shell form .grid {
    grid-template-columns: minmax(0, 1fr) !important;
  }
  [data-template-id] .waas-template-auth-shell form .col-span-2 {
    grid-column: auto !important;
    width: 100%;
  }
  [data-template-id] .waas-template-auth-shell #Register-signup-button {
    width: 100% !important;
  }
  @media (max-width: 900px) {
    [data-template-id] .waas-template-grid,
    [data-template-id] .waas-template-card-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
    [data-template-id] .waas-template-about-tile-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
    [data-template-id] .waas-template-benefit-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    [data-template-id] .waas-template-stat-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    [data-template-id] .waas-template-form-inline {
      grid-template-columns: minmax(0, 1fr) !important;
    }
    [data-template-id] .waas-template-grid > div,
    [data-template-id] .waas-template-grid > article {
      min-width: 0 !important;
    }
  }
  @media (max-width: 560px) {
    [data-template-id] .waas-template-stat-grid,
    [data-template-id] .waas-template-benefit-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
    [data-template-id] .waas-template-schedule-row {
      grid-template-columns: minmax(0, 1fr) !important;
    }
    [data-template-id] .waas-template-schedule-row a {
      justify-self: start !important;
    }
    [data-template-id] section {
      padding-left: 18px !important;
      padding-right: 18px !important;
    }
    [data-template-id] .waas-template-testimonial-mosaic figure:not(:first-child) {
      display: none !important;
    }
    [data-template-id] h1 {
      font-size: clamp(40px, 14cqw, 60px) !important;
    }
    [data-template-id] h2 {
      font-size: clamp(32px, 10cqw, 46px) !important;
    }
  }
`;
export {
  WaasAboutStorySection,
  WaasAuthSection,
  WaasCareerHeroSection,
  WaasContactSection,
  WaasFaqSection,
  WaasHeroSection,
  WaasImageGallerySection,
  WaasInstructorSpotlightSection,
  WaasNewsletterFooterSection,
  WaasOpenRolesSection,
  WaasPhilosophySection,
  WaasPricingPlansSection,
  WaasProcessStepsSection,
  WaasScheduleSection,
  WaasServiceDetailSection,
  WaasServicesGridSection,
  WaasStatsStripSection,
  WaasTestimonialsSection,
  WaasTrustedLogosSection,
  WaasWhyAvpsSection,
  waasResponsiveCss,
};
